import { useHistory } from 'react-router-dom';
import { gql, useApolloClient } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { message } from 'antd';
import {
	CREATE_BADGE_ASSIGNMENT,
	GENERATE_CERTIFICATE,
} from 'src/_shared/api/graphql/custom/recognition/recognitionMutations';

const GET_NOMINATION_CONFIG = gql`
	query GetNominationConfiguration($companyId: ID, $configId: ID!) {
		getNominationConfiguration(companyId: $companyId, configId: $configId) {
			id
			createdAt
			startDate
			status
			updatedAt
			badgeId
			badge {
				id
				points
				rewardBonus
				name
				isPreApproved
				allowSelfNomination
				certificateConfig {
					id
					name
					key
				}
			}
			companyId
			configId
			customFormId
			endDate
			awardedCount
			nominationConfigurationId
			nominatorEmployeeLevelId
			nominatorEmployeePayTypeId
			nominatorEmployeelogInTypeId
			nomineeEmployeeLevelId
			nomineeEmployeePayTypeId
			nomineeEmployeelogInTypeId
			snappyCampaignTierId
			snappyGrantId
		}
	}
`;

const CREATE_NOMINATION = gql`
	mutation CreateNomination($input: NominationInput!) {
		createNomination(input: $input) {
			updatedAt
			reason
			nomineeId
			nominee {
				id
				firstName
				lastName
			}
			nominatorId
			nominator {
				id
				firstName
				lastName
			}
			nominationReason
			nominationId
			nominationConfigurationId
			nominationConfiguration {
				id
				createdAt
				startDate
				status
				updatedAt
				badgeId
				companyId
				configId
				customFormId
				endDate
				awardedCount
				nominationConfigurationId
				nominatorEmployeeLevelId
				nominatorEmployeePayTypeId
				nominatorEmployeelogInTypeId
				nomineeEmployeeLevelId
				nomineeEmployeePayTypeId
				nomineeEmployeelogInTypeId
				snappyCampaignTierId
				snappyGrantId
			}
			itemType
			isWinner
			id
			createdAt
			configId
			companyId
			customFormAnswers
		}
	}
`;

const UPDATE_NOMINATION = gql`
	mutation UpdateNomination($input: NominationInput!) {
		updateNomination(input: $input) {
			updatedAt
			reason
			nomineeId
			nominee {
				id
				firstName
				lastName
			}
			nominatorId
			nominator {
				id
				firstName
				lastName
			}
			nominationReason
			nominationId
			nominationConfigurationId
			nominationConfiguration {
				id
				createdAt
				startDate
				status
				updatedAt
				badgeId
				companyId
				configId
				customFormId
				endDate
				awardedCount
				nominationConfigurationId
				nominatorEmployeeLevelId
				nominatorEmployeePayTypeId
				nominatorEmployeelogInTypeId
				nomineeEmployeeLevelId
				nomineeEmployeePayTypeId
				nomineeEmployeelogInTypeId
				snappyCampaignTierId
				snappyGrantId
			}
			itemType
			isWinner
			id
			createdAt
			configId
			companyId
			customFormAnswers
		}
	}
`;

const UPDATE_NOMINATION_CONFIG = gql`
	mutation UpdateNominationConfiguration(
		$input: NominationConfigurationInput!
	) {
		updateNominationConfiguration(input: $input) {
			id
			createdAt
			startDate
			status
			updatedAt
			badgeId
			companyId
			configId
			customFormId
			endDate
			awardedCount
			nominationConfigurationId
			nominatorEmployeeLevelId
			nominatorEmployeePayTypeId
			nominatorEmployeelogInTypeId
			nomineeEmployeeLevelId
			nomineeEmployeePayTypeId
			nomineeEmployeelogInTypeId
			snappyCampaignTierId
			snappyGrantId
		}
	}
`;

const QUERY_NOMINATIONS = gql`
	query QueryNominationsByConfigId(
		$configId: ID!
		$companyId: ID!
		$limit: Int
		$nextToken: String
	) {
		queryNominationsByConfigId(
			configId: $configId
			companyId: $companyId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				companyId
				configId
				createdAt
				customFormAnswers
				id
				isWinner
				itemType
				nominationConfigurationId
				nominationId
				nominationReason
				nominator {
					id
					firstName
					lastName
					title
					employeeId
					departmentId
				}
				nominatorId
				nominee {
					firstName
					id
					lastName
					title
					userGroupId
					subCompanyId
					employeeId
					departmentId
				}
				nomineeId
				reason
				updatedAt
			}
			nextToken
		}
	}
`;

const QUERY_NOMINATIONS_BY_NOMINEE_ID = gql`
	query QueryNominationsByNomineeId(
		$nomineeId: ID!
		$companyId: ID
		$configId: ID
		$limit: Int
		$nextToken: String
	) {
		queryNominationsByNomineeId(
			nomineeId: $nomineeId
			companyId: $companyId
			configId: $configId
			limit: $limit
			nextToken: $nextToken
		) {
			nextToken
			items {
				updatedAt
				reason
				nomineeId
				nominee {
					id
					firstName
					lastName
				}
				nominatorId
				nominator {
					id
					firstName
					lastName
				}
				nominationReason
				nominationId
				nominationConfigurationId
				nominationConfiguration {
					id
					createdAt
					startDate
					status
					updatedAt
					badgeId
					companyId
					configId
					customFormId
					endDate
					awardedCount
					nominationConfigurationId
					nominatorEmployeeLevelId
					nominatorEmployeePayTypeId
					nominatorEmployeelogInTypeId
					nomineeEmployeeLevelId
					nomineeEmployeePayTypeId
					nomineeEmployeelogInTypeId
					snappyCampaignTierId
					snappyGrantId
				}
				itemType
				isWinner
				id
				createdAt
				configId
				companyId
				customFormAnswers
			}
		}
	}
`;

const useNomineeWinnerList = (id) => {
	const history = useHistory();
	const [nomineeList, setNomineeList] = useState([]);
	const [nomineeVoterList, setNomineeVoterList] = useState([]);
	const [nomineeLoader, setNomineeLoader] = useState(false);
	const [nominationConfig, setNominationConfig] = useState({});
	const client = useApolloClient();
	const currentUser = useSelector((state) => state?.user?.currentUser);

	const getNomineeListById = async (id) => {
		setNomineeLoader(true);
		try {
			const { data, error } = await client.query({
				query: QUERY_NOMINATIONS,
				variables: {
					configId: id,
					companyId: currentUser.companyId,
					limit: 10000,
				},
				fetchPolicy: 'network-only',
			});
			setNomineeList(data?.queryNominationsByConfigId?.items);
			setNomineeLoader(false);
			return data?.queryNominationsByConfigId?.items;
		} catch (e) {
			console.error(e);
		}
	};

	const getNomineeVoterListById = async (configId, nomineeId) => {
		setNomineeLoader(true);
		try {
			const variables = {
				nomineeId: nomineeId,
				companyId: currentUser.companyId,
				limit: 10000,
			};
			if (configId) {
				variables.configId = configId;
			}
			const { data, error } = await client.query({
				query: QUERY_NOMINATIONS_BY_NOMINEE_ID,
				variables: variables,
				fetchPolicy: 'network-only',
			});
			setNomineeVoterList(data?.queryNominationsByNomineeId?.items);
			setNomineeLoader(false);
			return data?.queryNominationsByNomineeId?.items;
		} catch (e) {
			console.error(e);
		}
	};

	const getNominationConfigurationById = async (id) => {
		try {
			const { data, error } = await client.query({
				query: GET_NOMINATION_CONFIG,
				variables: {
					companyId: currentUser.companyId,
					configId: id,
				},
				fetchPolicy: 'network-only',
			});
			setNominationConfig(data?.getNominationConfiguration);
			return data?.getNominationConfiguration;
		} catch (e) {
			console.error(e);
		}
	};

	const selectWinner = async (
		nominations,
		nominationConfig,
		certificateFieldValuesMap = null
	) => {
		const isNotPreApproved = !nominationConfig?.badge?.isPreApproved;
		const requireAdminApproval =
			currentUser?.company?.recognitionSettings?.requireAdminApproval;
		try {
			// Update nominations as winners
			for (const nomination of nominations) {
				const payload = {
					id: nomination.id,
					configId: nominationConfig.id,
					createdAt: nomination.dateNominated,
					companyId: currentUser.companyId,
					isWinner: true,
				};
				const { error } = await client.mutate({
					mutation: UPDATE_NOMINATION,
					variables: {
						input: { ...payload },
					},
				});
				if (error) throw error;
			}

			// Create badge assignments for winners
			for (const nomination of nominations) {
				const payload = {
					itemType: 'BadgeAssignment',
					badgeId: nominationConfig.badgeId,
					companyId: currentUser.companyId,
					userId: nomination.nomineeId,
					awardedBy: currentUser.id,
					configId: nomination.configId,
					createdAt: nomination.dateNominated,
					recipients: [
						{
							userId: nomination.nomineeId,
						},
					],
					points: nominationConfig?.badge?.points,
					rewardBonus: nominationConfig?.badge?.rewardBonus,
					managerAssigned: false,
					content: {
						message: `You were nominated and chosen as a winner for ${nominationConfig?.badge?.name ? 'the "' + nominationConfig.badge.name + '"' : 'this'} Recognition!`,
						gifURL: null,
					},
					approvalStatus:
						isNotPreApproved || requireAdminApproval ? 'Pending' : 'Approved',
				};

				const { data, error } = await client.mutate({
					mutation: CREATE_BADGE_ASSIGNMENT,
					variables: {
						input: { ...payload },
					},
				});
				if (error) throw error;

				// Generate certificate if field values were provided for this winner
				if (certificateFieldValuesMap?.has(nomination.nomineeId)) {
					const badgeAssignmentId = data?.createBadgeAssignment?.id;
					const fieldValues = certificateFieldValuesMap.get(
						nomination.nomineeId
					);
					if (badgeAssignmentId && fieldValues) {
						try {
							await client.mutate({
								mutation: GENERATE_CERTIFICATE,
								variables: { badgeAssignmentId, fieldValues },
							});
						} catch (certError) {
							console.error('Error generating certificate:', certError);
							message.warning(
								`Certificate generation failed for ${nomination.nominee?.firstName || 'a winner'}. The recognition was still created.`
							);
						}
					}
				}
			}

			// Update nomination config as completed
			const { error } = await client.mutate({
				mutation: UPDATE_NOMINATION_CONFIG,
				variables: {
					input: {
						id: nominationConfig.id,
						companyId: currentUser.companyId,
						status: 'Complete',
					},
				},
			});
			if (error) throw error;
		} catch (e) {
			console.log(e);
		}

		history.push('/nominations');
		message.success('Winners Updated Successfully');
	};

	useEffect(() => {
		getNomineeListById(id);
		getNominationConfigurationById(id);
	}, [id]);

	return {
		getNomineeListById,
		selectWinner,
		getNomineeVoterListById,
		nomineeList,
		nominationConfig,
		loading: nomineeLoader,
		nomineeVoterList,
	};
};

export default useNomineeWinnerList;
